import map from 'lodash/map'
import remove from 'lodash/remove'
import sortBy from 'lodash/sortBy'
import cloneDeep from 'lodash/cloneDeep'
import intersection from 'lodash/intersection'
import forEachRight from 'lodash/forEachRight'
import { globalConfigStore } from 'kuaidong-store'
import { UnitUtil, DateUtil, Drill } from 'kuaidong-lib'
import { IFirstCategory, CompetitionType } from 'kuaidong-model'

const DrillMetricType2UnitMap = {
  score: {
    unit: '分',
    label: '得分',
  },
  count: {
    unit: '个',
    label: '个数',
  },
  millimeter: {
    unit: 'cm',
    label: '距离',
  },
}

const DrillFinishType2UnitMap = {
  score: {
    unit: '分',
    label: '得分',
  },
  count: {
    unit: '个',
    label: '个数',
  },
  distance: {
    unit: 'cm',
    label: '距离',
  },
}

export class DrillService {
  constructor(public model: Drill) {}

  get isRunning() {
    return this.model.playing_type === 'running'
  }

  get isKayaking2p() {
    return this.model.playing_type === 'Kayaking_2p'
  }

  public static isJumpRope(drillId: string) {
    return !!(drillId && drillId.startsWith('jump_rope_'))
  }

  public static buildScoreStr(amount: number, base: number, type: string, unit: string, _precision?: number) {
    if (type === 'duration') {
      return DateUtil.ms2Str(amount)
    }

    return `${UnitUtil.division(amount, base, _precision)}${unit}`
  }

  public static buildScoreStr2(type: string, drill: { score_count: number; score_duration: number; score: number }) {
    if (type === 'count') return drill.score_count + '个'
    if (type === 'second') return DateUtil.ms2Str(drill.score_duration)
    if (type === 'score') return drill.score + '分'
    return drill.score
  }

  public static buildMetricNameEn(type: string) {
    switch (type) {
      case 'duration':
        return 'time'
      default:
        return type
    }
  }

  public static buildScoreStrEn(type: string, score: number, duration: number) {
    switch (type) {
      case 'score':
        return score + ' points'
      case 'count':
        return score
      case 'distance':
        return UnitUtil.toFixed(score / 1000, 2) + ' m'
      case 'duration':
        return DateUtil.ms2Str(duration)
      default:
        return score
    }
  }

  public static buildScoreStr3(type: string, score: number) {
    switch (type) {
      case 'score':
        return score + '分'
      case 'score_count':
        return score + '个'
      case 'score_distance':
        return UnitUtil.toFixed(score / 1000, 2) + '米'
      case 'score_duration':
        return DateUtil.ms2Str(score)
      default:
        return score
    }
  }

  public static buildScoreStr4(drillId: string, score: number) {
    if (globalConfigStore && globalConfigStore.drills) {
      const drillInfo = globalConfigStore.drills[drillId]

      if (drillInfo) {
        return DrillService.buildScoreStr3(drillInfo.record_type, score)
      }
    }

    return score
  }

  public calcUnit() {
    if (this.model.metric_type !== 'duration') {
      return DrillMetricType2UnitMap[this.model.metric_type as keyof typeof DrillMetricType2UnitMap]
    } else {
      return DrillFinishType2UnitMap[this.model.finish_type as keyof typeof DrillFinishType2UnitMap]
    }
  }

  public static buildDrillOptions() {
    return globalConfigStore.drillItemOptions
  }

  public static defaultDrillOption() {
    const type = globalConfigStore.containerTypes[0]
    const drill = globalConfigStore.findDefaultDrill(type.name)

    return {
      containerType: type.name,
      drillId: drill?.drill_id,
    }
  }

  public static option2Name(option: any) {
    return globalConfigStore.findDrill(option.containerType, option.drillId)?.title
  }

  public static findOptionByDrillId(drillId: string) {
    const options = globalConfigStore.drillItemOptions

    for (const option of options) {
      for (const drill of option.children) {
        if (drill.value === drillId) {
          return {
            containerType: option.value,
            drillId: drill.value,
          }
        }
      }
    }

    return null
  }

  public static calcScoreChart(score: number, type: string) {
    if (score === -1) {
      return 0
    }

    if (type === 'score_duration') {
      return DateUtil.millisecond2minutes(score)
    }

    return score
  }

  public static calcScore(score: number, type: string) {
    if (score === -1) {
      return '-'
    }

    if (type === 'score_duration') {
      return DateUtil.ms2Str(score)
    }

    return score
  }

  public static calcScoreUnit(type: string) {
    if (type === 'score_duration') {
      return '分钟'
    }

    if (type === 'score_count') {
      return '个'
    }

    return '分'
  }

  /**
   * According to buildScoreStr2.
   * @param type
   * @param drill1
   * @param drill2
   * @returns
   */
  public static scoreSortMethod2(
    type: string,
    drill1: { score_count: number; score_duration: number; score: number },
    drill2: { score_count: number; score_duration: number; score: number }
  ) {
    if (type === 'count') return drill2.score_count - drill1.score_count
    if (type === 'second') return drill1.score_duration - drill2.score_duration
    if (type === 'score') return drill2.score - drill1.score
    return drill2.score - drill1.score
  }
}

export class DrillTagConfigService {
  public filterTag: IFirstCategory[] = []
  public firstTags: string[] = []
  public drillCategoryConfig: IFirstCategory[] = []
  public allCompetitionDrillIds: string[] = []

  constructor(public drillItemList: any[]) {}

  public async loadDrillConfig() {
    await globalConfigStore.ensureDrillCategoriesIsLoaded()
    this.drillCategoryConfig = globalConfigStore.drillCategories || []
    return this.drillCategoryConfig
  }

  public async filterConfigByDrillKey(keyName: string) {
    const drillIds = [...new Set(map(this.drillItemList, keyName))]
    const drillTagConfigClone = cloneDeep(await this.loadDrillConfig())
    drillTagConfigClone.forEach((item) => {
      if (item.children.length > 0) {
        forEachRight(drillTagConfigClone, (item) => {
          if (item.children.length > 0) {
            forEachRight(item.children, (subItem) => {
              if (intersection(drillIds, subItem.drill_ids).length === 0) {
                remove(item.children, subItem)
                if (item.children.length === 0) {
                  remove(drillTagConfigClone, item)
                }
              }
            })
          }
        })
      } else {
        remove(drillTagConfigClone, item)
      }
    })
    this.filterTag = drillTagConfigClone
    this.firstTags = ['全部'].concat(
      this.filterTag.map((item) => {
        return item.name
      })
    )
    return this.filterTag
  }

  public getAllCompetitionDrillIDs() {
    if (this.allCompetitionDrillIds.length === 0) {
      ;(globalConfigStore.drillCategories || []).forEach((firstTag) => {
        firstTag.children.forEach((subTag) => {
          this.allCompetitionDrillIds.push(...subTag.drill_ids)
        })
      })
    }

    return this.allCompetitionDrillIds
  }

  public filterCompetitionList(
    currentTag: string,
    currentDrill: {
      name: string
      label: string
      drill_ids: string[]
    }
  ): any {
    if (currentTag === '全部') {
      return this.sortCompetitionDrill(this.getAllCompetitionDrillIDs(), this.drillItemList)
    }
    if (currentTag !== '全部' && currentDrill.name === '全部') {
      const allSubDrills: string[] = []
      ;(this.filterTag.find((item) => {
        return item.name === currentTag
      }) as IFirstCategory).children.forEach((item) => allSubDrills.push(...item.drill_ids))
      return this.sortCompetitionDrill(allSubDrills, this.drillItemList)
    } else {
      return this.sortCompetitionDrill(currentDrill.drill_ids, this.drillItemList)
    }
  }

  public sortCompetitionDrill(drillIds: string[], competitionList: CompetitionType[]) {
    let res = competitionList.filter((item) => {
      return drillIds.includes(item.item_name)
    })
    res = sortBy(res, (drillItem) => {
      return drillIds.indexOf(drillItem.item_name)
    })
    return res
  }

  public getSecondTags(currentTag: string) {
    if (currentTag === '全部') {
      return null
    } else {
      const currentFirstTag = this.filterTag.find((item) => item.name === currentTag)
      if (currentFirstTag && !currentFirstTag.children.find((item) => item.name === '全部')) {
        const allDrillIds: string[] = []
        currentFirstTag.children.forEach((item) => {
          allDrillIds.push(...item.drill_ids)
        })
        currentFirstTag.children.unshift({ name: '全部', label: '全部', drill_ids: allDrillIds })
      }
      return currentFirstTag ? currentFirstTag.children : []
    }
  }
}
